import React from "react";
import { Typography, Spin, Row, Col, Progress } from "antd";
import {
  DashboardOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
} from "@ant-design/icons";
import { AiFillDashboard } from "react-icons/ai";
import { useLocation } from "react-router-dom";

const { Title } = Typography;

const FAULT_TYPES = [
  "No Faults",
  "Rich Mixture",
  "lean Mixture",
  "Low Voltage",
];

const FAULT_CAUSES = [
  ["Vehicle health is normal"],
  [
    "Incorrect Sensor performance",
    "High fuel pressure",
    "Defective injector",
    "Faulty pressure regulator",
    "Clogged air filter",
    "Clogged fuel return line",
  ],
  [
    "Incorrect sensor performance",
    "Low fuel pressure",
    "Defective injector",
    "Faulty pressure regulator",
  ],
  [
    "Worn spark plugs",
    "Faulty ignition cables",
    "Defective coil",
    "Faulty sensor wiring",
  ],
];

const FaultDetection = () => {
  const location = useLocation();
  const { faultData, serialNo } = location.state || {};
  const timestamp = faultData?.timestamp;

  const getISTTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";

    try {
      const date = new Date(timestamp);
      const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
      return istDate.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return timestamp;
    }
  };

  const vehicleData = faultData?.vehicledata || {};
  const prediction = faultData?.prediction || {};

  const fault = prediction.enginefault || 0;

  const topOutlierFeatures =
    fault !== 0 ? prediction.topoutlierfeatures || [] : [];

  const dashboardState = {
    fault,
    isOutlier: prediction.isoutlier || false,
    outliers: topOutlierFeatures,
    faultCauses: FAULT_CAUSES[fault],
    faultType: FAULT_TYPES[fault],
  };

  const vehicleDataState = {
    speed: vehicleData.speed,
    engineSpeed: vehicleData.enginerpm,
    torque: vehicleData.force,
    power: vehicleData.power,
    fuelLevel: vehicleData.fuellevel,
    fuelRate: vehicleData.consumptionlh,
    averageConsumption: vehicleData.consumptionl100km,
    ect: vehicleData.enginecoolanttemp,
    tps: vehicleData.tps ? parseFloat(vehicleData.tps).toFixed(3) : 0,
    map: vehicleData.map,
    emissionsCO2: vehicleData.co2,
  };

  const featureNameMapping = {
    speed: "Speed",
    enginerpm: "Engine RPM",
    force: "Force",
    power: "Power",
    consumptionlh: "Consumption L/H",
    consumptionl100km: "Consumption L/100KM",
    tps: "TPS",
    map: "MAP",
    co2: "CO2",
  };

  const getOutliersStatus = (featureName) => {
    if (!dashboardState.fault) return { color: "#070707ff" };

    const isOutlier = dashboardState.outliers.some(
      (outlier) =>
        outlier.toLowerCase() === featureName.toLowerCase() ||
        featureNameMapping[featureName]?.toLowerCase() === outlier.toLowerCase()
    );

    if (isOutlier) {
      if (dashboardState.isOutlier) {
        return { color: "#ff4d4f" }; 
      } else {
        return { color: "#faad14" }; 
      }
    }
    return { color: "#070707ff" };
  };

  const getTemperatureStatus = (temp) => {
    if (temp > 104) return { color: "#ff4d4f", status: "High Temperature" };
    if (temp >= 90 && temp <= 104)
      return { color: "#52c41a", status: "Normal Temperature" };
    return { color: "#faad14", status: "Low Temperature" };
  };

  const getEmissionsStatus = (co2) => {
    if (co2 > 200) return "High Emissions";
    if (co2 > 150) return "Moderate Emissions";
    return "Low Emissions";
  };

  const MetricCard = ({ title, value, unit, icon, outlierStatus }) => (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "8px",
        padding: "16px",
        border: "1px solid #e8e8e8",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "8px",
        }}
      >
        <div style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
          {title}
        </div>
        <div style={{ fontSize: "16px", color: "#999" }}>{icon}</div>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
        <div
          style={{
            fontSize: "24px",
            fontWeight: "600",
            lineHeight: "1",
            color: outlierStatus?.color || "#333",
          }}
        >
          {value || 0}
        </div>
        <div style={{ fontSize: "14px", color: "#666", marginLeft: "4px" }}>
          {unit}
        </div>
      </div>
    </div>
  );

  const EnginePerformanceCard = () => (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "12px",
        padding: "20px",
        border: "1px solid #e8e8e8",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}
      >
        <DashboardOutlined
          style={{ fontSize: "18px", color: "#1890ff", marginRight: "8px" }}
        />
        <h3
          style={{
            color: "#333",
            margin: 0,
            fontSize: "16px",
            fontWeight: "600",
          }}
        >
          Engine Performance
        </h3>
      </div>

      <Row gutter={[12, 12]}>
        <Col span={12}>
          <MetricCard
            title="Speed"
            value={vehicleDataState.speed}
            unit="km/h"
            icon={<AiFillDashboard />}
            outlierStatus={getOutliersStatus("speed")}
          />
        </Col>
        <Col span={12}>
          <MetricCard
            title="Engine Speed"
            value={vehicleDataState.engineSpeed}
            unit="RPM"
            icon={<DashboardOutlined />}
            outlierStatus={getOutliersStatus("enginerpm")}
          />
        </Col>
        <Col span={12}>
          <MetricCard
            title="Torque"
            value={vehicleDataState.torque}
            unit="Nm"
            icon={<ThunderboltOutlined />}
            outlierStatus={getOutliersStatus("force")}
          />
        </Col>
        <Col span={12}>
          <MetricCard
            title="Power"
            value={vehicleDataState.power}
            unit="kW"
            icon={<ThunderboltOutlined />}
            outlierStatus={getOutliersStatus("power")}
          />
        </Col>
      </Row>
    </div>
  );

  const FuelSystemCard = () => (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "12px",
        padding: "20px",
        border: "1px solid #e8e8e8",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}
      >
        <FireOutlined
          style={{ fontSize: "18px", color: "#ff4d4f", marginRight: "8px" }}
        />
        <h3
          style={{
            color: "#333",
            margin: 0,
            fontSize: "16px",
            fontWeight: "600",
          }}
        >
          Fuel System
        </h3>
      </div>

      <Row gutter={[12, 12]}>
        <Col span={24}>
          <div
            style={{
              background: "#f9f9f9",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                color: "#333",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Fuel Level
            </div>
            <Progress
              percent={vehicleDataState.fuelLevel || 0}
              status={vehicleDataState.fuelLevel < 20 ? "exception" : "normal"}
              strokeColor={
                vehicleDataState.fuelLevel < 20 ? "#ff4d4f" : "#52c41a"
              }
              trailColor="#e8e8e8"
              strokeWidth={8}
              style={{ marginBottom: "4px" }}
            />
            <div style={{ color: "#666", fontSize: "12px" }}>
              {vehicleDataState.fuelLevel < 20 ? "Low Fuel" : "Normal"}
            </div>
          </div>
        </Col>
        <Col span={12}>
          <MetricCard
            title="Fuel Rate"
            value={vehicleDataState.fuelRate}
            unit="L/h"
            icon={<FireOutlined />}
            outlierStatus={getOutliersStatus("consumptionlh")}
          />
        </Col>
        <Col span={12}>
          <MetricCard
            title="Avg Consumption"
            value={vehicleDataState.averageConsumption}
            unit="L/100km"
            icon={<DashboardOutlined />}
            outlierStatus={getOutliersStatus("consumptionl100km")}
          />
        </Col>
      </Row>
    </div>
  );

  const EngineDiagnosticsCard = () => {
    const tempStatus = getTemperatureStatus(vehicleDataState.ect);

    return (
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: "20px",
          border: "1px solid #e8e8e8",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <WarningOutlined
            style={{ fontSize: "18px", color: "#faad14", marginRight: "8px" }}
          />
          <h3
            style={{
              color: "#333",
              margin: 0,
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            Engine Diagnostics
          </h3>
        </div>

        <Row gutter={[12, 12]}>
          <Col span={12}>
            <div
              style={{
                background: "#f9f9f9",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  color: "#666",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Engine Temp
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "4px",
                  marginBottom: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: tempStatus.color,
                  }}
                >
                  {vehicleDataState.ect || 0}
                </div>
                <div style={{ fontSize: "14px", color: "#666" }}>°C</div>
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                }}
              >
                {tempStatus.status}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <MetricCard
              title="Throttle Position"
              value={vehicleDataState.tps}
              unit="%"
              icon={<DashboardOutlined />}
              outlierStatus={getOutliersStatus("tps")}
            />
          </Col>
          <Col span={24}>
            <MetricCard
              title="Manifold Absolute Pressure"
              value={vehicleDataState.map}
              unit="kPa"
              icon={<DashboardOutlined />}
              outlierStatus={getOutliersStatus("map")}
            />
          </Col>
        </Row>
      </div>
    );
  };

  const EmissionsCard = () => (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "12px",
        padding: "20px",
        border: "1px solid #e8e8e8",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}
      >
        <ExclamationCircleOutlined
          style={{ fontSize: "18px", color: "#52c41a", marginRight: "8px" }}
        />
        <h3
          style={{
            color: "#333",
            margin: 0,
            fontSize: "16px",
            fontWeight: "600",
          }}
        >
          Emissions
        </h3>
      </div>

      <MetricCard
        title="CO2 Emissions"
        value={vehicleDataState.emissionsCO2}
        unit="g/km"
        icon={<ExclamationCircleOutlined />}
        outlierStatus={getOutliersStatus("co2")}
      />

      <div
        style={{
          marginTop: "12px",
          padding: "8px 12px",
          backgroundColor: "#f9f9f9",
          borderRadius: "6px",
          color: "#666",
          fontSize: "14px",
          textAlign: "center",
        }}
      >
        {getEmissionsStatus(vehicleDataState.emissionsCO2)}
      </div>
    </div>
  );

  const FaultAnalysisCard = () => (
    <div
      style={{
        marginBottom: 24,
        borderRadius: 12,
        backgroundColor: "#ffffff",
        border: "1px solid #e8e8e8",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        padding: "20px",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}
      >
        <WarningOutlined
          style={{ fontSize: "18px", color: "#ff4d4f", marginRight: "8px" }}
        />
        <h3
          style={{
            color: "#333",
            margin: 0,
            fontSize: "16px",
            fontWeight: "600",
          }}
        >
          Engine Fault Analysis
        </h3>
      </div>

      <div
        style={{
          padding: "12px 16px",
          borderRadius: "8px",
          backgroundColor:
            dashboardState.faultType === "No Faults" ? "#f6ffed" : "#fff2f0",
          border: `1px solid ${
            dashboardState.faultType === "No Faults" ? "#b7eb8f" : "#ffccc7"
          }`,
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
        }}
      >
        {dashboardState.faultType === "No Faults" ? (
          <CheckCircleOutlined
            style={{ color: "#52c41a", marginRight: "8px" }}
          />
        ) : (
          <ExclamationCircleOutlined
            style={{ color: "#ff4d4f", marginRight: "8px" }}
          />
        )}
        <span style={{ color: "#333", fontWeight: "500" }}>
          Fault Type: {dashboardState.faultType}
        </span>
      </div>

      {dashboardState.faultType !== "No Faults" &&
        dashboardState.faultCauses.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: "#333", fontWeight: "600", marginBottom: 8 }}>
              Possible Causes:
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, color: "#666" }}>
              {dashboardState.faultCauses.map((cause, i) => (
                <li key={i} style={{ marginBottom: 4 }}>
                  {cause}
                </li>
              ))}
            </ul>
          </div>
        )}

      {dashboardState.faultType !== "No Faults" &&
        dashboardState.outliers.length > 0 && (
          <div
            style={{
              padding: "16px",
              borderRadius: "8px",
              backgroundColor: "#f0f5ff",
              border: "1px solid #adc6ff",
            }}
          >
            <div style={{ color: "#333", fontWeight: "600", marginBottom: 12 }}>
              Outlier Detection Status
            </div>

            <div style={{ marginBottom: 12 }}>
              <div
                style={{ color: "#333", fontWeight: "500", marginBottom: 8 }}
              >
                Detected Outlier Features:
              </div>
              <div style={{ marginBottom: 12 }}>
                {dashboardState.outliers.map((feature, index) => (
                  <span
                    key={index}
                    style={{
                      display: "inline-block",
                      margin: "2px 4px",
                      padding: "4px 8px",
                      backgroundColor: dashboardState.isOutlier
                        ? "#ffebe6"
                        : "#fff7e6",
                      border: `1px solid ${
                        dashboardState.isOutlier ? "#ff4d4f" : "#faad14"
                      }`,
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: dashboardState.isOutlier ? "#ff4d4f" : "#faad14",
                      fontWeight: "500",
                    }}
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div
                style={{ color: "#333", fontWeight: "500", marginBottom: 8 }}
              >
                Color Legend:
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  flexWrap: "wrap",
                  fontSize: "14px",
                  color: "#666",
                }}
              >
                <span>
                  <span style={{ color: "#ff4d4f" }}>●</span> Critical Outliers
                  (Contributing to fault)
                </span>
                <span>
                  <span style={{ color: "#faad14" }}>●</span> Normal Range
                  Outliers (Potential contributors)
                </span>
                <span>
                  <span style={{ color: "#070707ff" }}>●</span> Normal Values
                </span>
              </div>
            </div>

            {!dashboardState.isOutlier &&
              dashboardState.faultType !== "No Faults" && (
                <div
                  style={{
                    fontSize: "14px",
                    fontStyle: "italic",
                    color: "#faad14",
                    backgroundColor: "#fffbe6",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #ffe58f",
                  }}
                >
                  Note: Outlier features are currently in normal range but may
                  have contributed to the fault detection.
                </div>
              )}
          </div>
        )}
    </div>
  );

  const VehicleInfoCard = () => (
    <div
      style={{
        marginBottom: 24,
        borderRadius: 12,
        backgroundColor: "#ffffff",
        border: "1px solid #e8e8e8",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        padding: "20px",
      }}
    >
      <Row gutter={16}>
        <Col span={12}>
          <div>
            <div
              style={{
                color: "#666",
                fontSize: 14,
                fontWeight: "500",
                marginBottom: 4,
              }}
            >
              Serial Number
            </div>
            <div
              style={{
                fontSize: 18,
                fontFamily: "monospace",
                color: "#1890ff",
                fontWeight: "600",
              }}
            >
              {serialNo || "N/A"}
            </div>
          </div>
        </Col>
        <Col span={12}>
          <div>
            <div
              style={{
                color: "#666",
                fontSize: 14,
                fontWeight: "500",
                marginBottom: 4,
              }}
            >
              Timestamp (IST)
            </div>
            <div style={{ fontSize: 16, color: "#1890ff", fontWeight: "600" }}>
              {getISTTimestamp(timestamp)}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  return (
    <div style={{ background: "#f0f2f5", minHeight: "100vh", padding: "24px" }}>
      <div
        style={{
          marginBottom: 24,
          borderRadius: 12,
          backgroundColor: "#ffffff",
          border: "1px solid #e8e8e8",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          padding: "20px",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 20 }}
        >
          <DashboardOutlined
            style={{ fontSize: "20px", color: "#1890ff", marginRight: "10px" }}
          />
          <Title
            level={2}
            style={{
              margin: 0,
              color: "#333",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            Fault Details Dashboard
          </Title>
        </div>
        <VehicleInfoCard />

        {!faultData && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        )}
        {dashboardState.faultType && <FaultAnalysisCard />}

        {vehicleDataState && (
          <div
            style={{
              marginTop: 16,
              padding: "20px",
              background: "#ffffff",
              borderRadius: "12px",
              border: "1px solid #e8e8e8",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Title level={3} style={{ color: "#333", marginBottom: 20 }}>
              Vehicle Analytics Dashboard
            </Title>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <EnginePerformanceCard />
              </Col>
              <Col span={12}>
                <FuelSystemCard />
              </Col>
              <Col span={12}>
                <EngineDiagnosticsCard />
              </Col>
              <Col span={12}>
                <EmissionsCard />
              </Col>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaultDetection;
